import api from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { toast } from "sonner";
import RuleChainEditor from "./RuleChainEditor";

/**
 * RuleEditorDialog — create-or-edit modal for a single automation rule.
 * Contains the primary IF-condition + THEN-action pair PLUS the
 * composable action chain builder (RuleChainEditor).
 *
 * Extracted from SellerDropship.jsx in Phase 3 refactor.
 */
export default function RuleEditorDialog({
  ruleDraft, setRuleDraft,
  rules, setRules, rulesMeta,
  settings, playbooks,
}) {
  if (!ruleDraft) return null;
  return (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setRuleDraft(null)}>
    <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="rule-editor-dialog">
      <div className="text-[15px] font-semibold">{ruleDraft.id ? "Edit rule" : "New routing rule"}</div>
      <Input placeholder="Rule name" value={ruleDraft.name || ""}
        onChange={e => setRuleDraft(d => ({ ...d, name: e.target.value }))}
        className="h-10 font-medium" data-testid="rule-name"/>

      <div className="rounded-xl border border-border p-4 space-y-2">
        <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">IF</div>
        <div className="grid grid-cols-3 gap-2">
          <select value={ruleDraft.condition.field}
            onChange={e => setRuleDraft(d => ({ ...d, condition: { ...d.condition, field: e.target.value } }))}
            data-testid="rule-field"
            className="h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
            {Object.entries(rulesMeta.allowed_fields).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={ruleDraft.condition.op}
            onChange={e => setRuleDraft(d => ({ ...d, condition: { ...d.condition, op: e.target.value } }))}
            data-testid="rule-op"
            className="h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
            {Object.entries(rulesMeta.allowed_ops).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Input value={Array.isArray(ruleDraft.condition.value) ? ruleDraft.condition.value.join(",") : ruleDraft.condition.value}
            onChange={e => setRuleDraft(d => ({
              ...d,
              condition: {
                ...d.condition,
                value: (d.condition.op === "in" || d.condition.op === "nin")
                  ? e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                  : e.target.value,
              },
            }))}
            data-testid="rule-value"
            placeholder={(ruleDraft.condition.op === "in" || ruleDraft.condition.op === "nin") ? "CA, OR, WA" : "value"}
            className="h-10 mono"/>
        </div>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">THEN</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Route to carrier</label>
            <select value={ruleDraft.action.carrier || ""}
              onChange={e => setRuleDraft(d => ({ ...d, action: { ...d.action, carrier: e.target.value } }))}
              data-testid="rule-carrier"
              className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
              <option value="">— any (rate-shop)</option>
              <option value="usps">USPS</option>
              <option value="ups">UPS</option>
              <option value="fedex">FedEx</option>
              <option value="dhl_express">DHL Express</option>
            </select>
          </div>
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Service token (optional)</label>
            <Input value={ruleDraft.action.service_token || ""}
              onChange={e => setRuleDraft(d => ({ ...d, action: { ...d.action, service_token: e.target.value } }))}
              placeholder="e.g., usps_priority"
              className="h-10 mono"/>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!ruleDraft.action.add_insurance}
            onChange={e => setRuleDraft(d => ({ ...d, action: { ...d.action, add_insurance: e.target.checked } }))}
            data-testid="rule-add-insurance"
            className="w-4 h-4 accent-foreground"/>
          <span className="text-[13px]">Also add insurance to this label</span>
        </label>
        {ruleDraft.action.add_insurance && (
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Minimum insurance amount ($)</label>
            <Input type="number" min="0" step="10"
              value={ruleDraft.action.min_insurance || ""}
              onChange={e => setRuleDraft(d => ({ ...d, action: { ...d.action, min_insurance: Number(e.target.value || 0) } }))}
              className="h-10 mono w-40"/>
          </div>
        )}
      </div>

      {/* Composable action chain — Option A · Pt 3 */}
      <RuleChainEditor
        ruleDraft={ruleDraft}
        setRuleDraft={setRuleDraft}
        rulesMeta={rulesMeta}
        settings={settings}
        playbooks={playbooks}
      />

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={() => setRuleDraft(null)}
          className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
          Cancel
        </button>
        <button
          onClick={async () => {
            const body = {
              name: ruleDraft.name || "New rule",
              priority: ruleDraft.priority || 100,
              active: ruleDraft.active !== false,
              condition: ruleDraft.condition,
              action: {
                carrier:       ruleDraft.action.carrier || null,
                service_token: ruleDraft.action.service_token || null,
                add_insurance: !!ruleDraft.action.add_insurance,
                min_insurance: ruleDraft.action.min_insurance || null,
              },
              actions: (ruleDraft.actions || []).filter(s => s && s.type),
            };
            try {
              if (ruleDraft.id) {
                await api.put(`/dropship/automation-rules/${ruleDraft.id}`, body);
              } else {
                await api.post("/dropship/automation-rules", body);
              }
              const { data } = await api.get("/dropship/automation-rules");
              setRules(data?.rules || []);
              setRuleDraft(null);
              toast.success(`Rule ${ruleDraft.id ? "updated" : "created"}`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Could not save rule");
            }
          }}
          data-testid="rule-save"
          className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
          {ruleDraft.id ? "Save changes" : "Create rule"}
        </button>
      </div>
    </div>
  </div>
  );
}
